import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { navLinks } from "../../config/navLinks";
import { useAppDispatch, useAppSelector } from "../../core/redux/reduxStore";
import { ActionUI } from "../../core/redux";
import { Drawer } from "rsuite";
import ArrowDownLineIcon from "@rsuite/icons/ArrowDownLine";
import ArrowUpLineIcon from "@rsuite/icons/ArrowUpLine";
import {
  SvgSignOut,
  SvgProfile,
  SvgChangePasswordKey,
} from "../../assets/icons/index";
import { AppRoutesUI } from "../../config/appRoutes";
import Reganalytics from "./../../assets/images/Reganalytics.png";

type NavButtonProps = {
  label: string;
  path: string;
  svgIcon?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  >;
  activeOnPaths?: string[];
};

const NavButton = (props: NavButtonProps): JSX.Element => {
  const routeLocation = useLocation();
  const isActive = routeLocation.pathname.startsWith(props.path);
  const dispatch = useAppDispatch();

  return (
    <li className="nav-item">
      <Link
        to={props.path}
        className={`nav-link${isActive ? " active" : ""} text-decoration-none`}
        aria-current="page"
        onClick={() => dispatch(ActionUI.closeSideNav())}
      >
        <div className="d-flex align-item-center ">
          <div className="svg-container">
            {props.svgIcon && <props.svgIcon />}
          </div>
          <p>{props.label}</p>
        </div>
      </Link>
    </li>
  );
};

const DropdownButton = (props: Partial<NavButtonProps>): JSX.Element => {
  const routeLocation = useLocation();
  const isActive = props.path && routeLocation.pathname.startsWith(props.path);
  const dispatch = useAppDispatch();

  return (
    <li className="dropdown-item p-0">
      <Link
        to={props.path || "#"}
        className={`ps-3 pt-2 pb-2 m-0 d-block text-decoration-none dropdown-link${
          isActive ? " active" : ""
        }`}
        aria-current="page"
        onClick={() => dispatch(ActionUI.closeSideNav())}
      >
        <div className="svg-container">
          {props.svgIcon && <props.svgIcon />} {props.label}
        </div>
      </Link>
    </li>
  );
};

const NavButtonDropdown = (
  props: React.PropsWithChildren<Partial<NavButtonProps>>
): JSX.Element => {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(
    !props.activeOnPaths?.some((item) => pathname.startsWith(item))
  );

  useEffect(
    () =>
      setCollapsed(
        !props.activeOnPaths?.some((item) => pathname.startsWith(item))
      ),
    [pathname, props.activeOnPaths]
  );

  return (
    <li className="nav-item">
      <div
        className={`nav-link py-2 p-0 mb-0 click text-decoration-none${
          collapsed ? "" : " active"
        }`}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="d-flex side-nav-dropdown-header align-item-center">
          <div className="svg-container">
            {props.svgIcon && <props.svgIcon />}
          </div>
          <p className="d-flex justify-content-between w-100 align-items-center">
            {props.label}
            {collapsed ? <ArrowDownLineIcon /> : <ArrowUpLineIcon />}
          </p>
        </div>
        <div className={`dropdown dropdown-${collapsed ? "hidden" : "show"}`}>
          {props.children}
        </div>
      </div>
    </li>
  );
};

export const SideNav = () => {
  const { email } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const { isSiteNavOpen, showSiteNav } = useAppSelector((state) => state.ui);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const {menuItems} = useAppSelector((state) => state.menuByRole);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
      dispatch(ActionUI.closeSideNav());
    };

    window.addEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return !isMobile ? (
    <div className={`site-nav ${showSiteNav ? "" : "display-none"}`}>
      <ul className="nav flex-column">
        {menuItems.map((item) =>
          item.children ? (
            <NavButtonDropdown
              key={item.id}
              label={item.displayName}
              svgIcon={navLinks[item.id].icon}
              activeOnPaths={navLinks[item.id].activeOnPaths}
            >
              <ul className="dropdown-nav-items p-0">
                {item.children.map((dropdownItem) => (
                  <DropdownButton
                    key={dropdownItem.id}
                    label={dropdownItem.displayName}
                    svgIcon={navLinks[dropdownItem.id].icon}
                    path={navLinks[dropdownItem.id].path}
                  />
                ))}
              </ul>
            </NavButtonDropdown>
          ) : (
            <NavButton
              key={item.id}
              label={item.displayName}
              path={navLinks[item.id].path}
              svgIcon={navLinks[item.id].icon}
            />
          )
        )}
      </ul>
    </div>
  ) : (
    <Drawer
      open={isSiteNavOpen}
      onClose={() => dispatch(ActionUI.closeSideNav())}
      placement="left"
      size="xs"
      className="drawer-style"
      autoFocus={true}
    >
      <Drawer.Body className="site-nav d-flex flex-column">
        <div className="d-flex flex-row-reverse mb-2">
          <p
            className="white fs-7 me-3"
            onClick={() => dispatch(ActionUI.closeSideNav())}
          >
            X
          </p>
        </div>
        <div className="d-flex flex-column align-items-center justify-content-center">
          <img src={Reganalytics} alt="Reganalytics Logo"></img>
          <p className="white fs-7">{email}</p>
          <strong className="white fs-7">Super Admin</strong>
          <hr />
        </div>
        <ul className="nav flex-column">
          {menuItems.map((item) =>
            item.children ? (
              <NavButtonDropdown
                key={item.id}
                label={item.displayName}
                svgIcon={navLinks[item.id].icon}
                activeOnPaths={navLinks[item.id].activeOnPaths}
              >
                <ul className="dropdown-nav-items p-0">
                  {item.children.map((dropdownItem) => (
                    <DropdownButton
                      key={dropdownItem.id}
                      label={dropdownItem.displayName}
                      svgIcon={navLinks[dropdownItem.id].icon}
                      path={navLinks[dropdownItem.id].path}
                    />
                  ))}
                </ul>
              </NavButtonDropdown>
            ) : (
              <NavButton
                key={item.id}
                label={item.displayName}
                path={navLinks[item.id].path}
                svgIcon={navLinks[item.id].icon}
              />
            )
          )}
        </ul>
        <div className="d-flex flex-column mt-3">
          <hr />
          <Link to={AppRoutesUI.SuperAdmin.SuperAdminProfile()}>
            <div
              className="d-flex flex-row px-3 py-2 align-items-center"
              onClick={() => dispatch(ActionUI.closeSideNav())}
            >
              <SvgProfile />
              <p className="white fs-7 px-3">Profile</p>
            </div>
          </Link>
          <Link to={AppRoutesUI.SuperAdmin.SuperAdminChangePassword()}>
            <div
              className="d-flex flex-row px-3 py-2 align-items-center"
              onClick={() => dispatch(ActionUI.closeSideNav())}
            >
              <SvgChangePasswordKey />
              <p className="white fs-7 px-3">Change Password</p>
            </div>
          </Link>
          <div
            className="d-flex flex-row px-3 py-2 align-items-center"
            onClick={() => dispatch(ActionUI.closeSideNav())}
          >
            <SvgSignOut />
            <p className="white fs-7 px-3">Sign Out</p>
          </div>
        </div>
      </Drawer.Body>
    </Drawer>
  );
};
